'use client';

import { useTranslations } from 'next-intl';
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/ui-kit/utils';
import { MIN_SEARCH_LENGTH, PANEL_MOTION_MS, TAB_PREFIXES } from './core/constants';
import { rememberQuery } from './core/recentStore';
import { useSearchPlaceholders, useSearchSection } from './core/useSearchPlaceholders';
import { HeaderSearchField } from './HeaderSearchField';
import { useKeyFlash } from './hooks/useKeyFlash';
import { useResultNavigation } from './hooks/useResultNavigation';
import { useSearchState } from './hooks/useSearchState';
import { ResultsFrame } from './ResultsFrame';
import { SearchBody } from './SearchBody';
import {
  DROPDOWN_CLOSED_CLASS,
  DROPDOWN_OPEN_CLASS,
  PANEL_CLOSE_EASE_CLASS,
  PANEL_EASE_CLASS,
} from './shared/motion';
import { SearchHints } from './shared/SearchHints';
import { isTypingTarget } from './utils/keyboard';

interface SearchPopoverV2Props {
  className?: string;
}

/**
 * The header's universal search: one field over pairs, prediction markets,
 * traders and news. ⌘K, / or a click grows the 170px field in place into a
 * 400px panel over the nav while the page blurs; Esc folds it back. The
 * keyboard does the rest: arrows and Enter over the rows, Tab over the tabs,
 * `@` and `$` to jump to traders and pairs.
 */
export function SearchPopoverV2({ className }: SearchPopoverV2Props) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [entranceKey, setEntranceKey] = useState(0);
  const openRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const t = useTranslations('search');
  const listId = useId();
  const search = useSearchState(value);
  const { tab, setTab, cycleTab, searching, results } = search;
  const placeholders = useSearchPlaceholders(useSearchSection());
  const { hitKey, flash } = useKeyFlash();
  const navigation = useResultNavigation(
    listRef,
    contentRef,
    `${entranceKey}:${searching}:${tab}:${value}`,
  );

  // The effects below listen through these two, so they come first.
  const openPanel = useCallback(() => {
    if (openRef.current) return;
    openRef.current = true;
    clearTimeout(resetTimer.current);
    setEntranceKey((key) => key + 1);
    setOpen(true);
  }, []);

  const closePanel = useCallback(
    (reset = false) => {
      openRef.current = false;
      setOpen(false);
      inputRef.current?.blur();
      // The panel keeps its content while it folds back, then starts over.
      if (!reset) return;
      resetTimer.current = setTimeout(() => {
        setValue('');
        setTab('all');
      }, PANEL_MOTION_MS);
    },
    [setTab],
  );

  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      // Below 1024 the header hides the field; the bottom bar has its own search.
      if (!rootRef.current?.offsetParent) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        flash('shortcut');
        if (openRef.current) {
          closePanel();
          return;
        }
        openPanel();
        inputRef.current?.focus();
        return;
      }
      if (e.key === '/' && !openRef.current && !isTypingTarget(e.target)) {
        e.preventDefault();
        openPanel();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closePanel, flash, openPanel]);

  // A press anywhere off the panel — the blurred page included — closes it.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (e.target instanceof Node && boxRef.current?.contains(e.target)) return;
      closePanel();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, closePanel]);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleResultOpened = useCallback(() => closePanel(true), [closePanel]);

  // `@nickname` and `$ticker`: the first character picks the tab and is not searched.
  const handleValueChange = (next: string) => {
    const prefixTab = TAB_PREFIXES[next.charAt(0)];
    if (!prefixTab) {
      setValue(next);
      return;
    }
    setTab(prefixTab);
    setValue(next.slice(1));
  };

  const clearSearch = () => {
    setValue('');
    setTab('all');
  };

  const handleClear = () => {
    clearSearch();
    inputRef.current?.focus();
  };

  // Enter opens the highlighted row; with nothing to open, it keeps the query as a recent search.
  const openActiveRow = (inNewTab: boolean) => {
    const link = navigation.activeLink();
    if (!link) {
      if (value.trim().length >= MIN_SEARCH_LENGTH) rememberQuery(value);
      return;
    }
    if (inNewTab) window.open(link.href, '_blank', 'noopener');
    else link.click();
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        flash('down');
        navigation.move(1);
        return;
      case 'ArrowUp':
        e.preventDefault();
        flash('up');
        navigation.move(-1);
        return;
      case 'Enter':
        e.preventDefault();
        flash('enter');
        openActiveRow(e.metaKey || e.ctrlKey);
        return;
      case 'Tab':
        e.preventDefault();
        flash('tab');
        cycleTab(e.shiftKey ? -1 : 1);
        return;
      case 'Escape':
        e.preventDefault();
        flash('esc');
        if (value || tab !== 'all') clearSearch();
        else closePanel();
        return;
      case 'Backspace':
        if (!value && tab !== 'all') setTab('all');
    }
  };

  // Clicks inside keep the caret in the field; the list's own scrollbar still drags.
  const handleBoxMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === inputRef.current || e.target === listRef.current) return;
    e.preventDefault();
    if (openRef.current) return;
    openPanel();
    inputRef.current?.focus();
  };

  const renderList = (content: ReactNode) => (
    <ResultsFrame
      listRef={listRef}
      contentRef={contentRef}
      listId={listId}
      highlight={navigation.highlight}
      refreshing={searching && results.refreshing}
      onPointerMove={navigation.handlePointerMove}
    >
      {content}
    </ResultsFrame>
  );

  return (
    <div ref={rootRef} className={cn('relative z-[2] h-8 w-full', className)}>
      {/* The page under the open panel blurs; its first click only closes the search. */}
      <div
        aria-hidden
        className={cn(
          'fixed inset-0 transition-[backdrop-filter]',
          open ? PANEL_EASE_CLASS : PANEL_CLOSE_EASE_CLASS,
          open ? 'backdrop-blur-[8px]' : 'pointer-events-none backdrop-blur-[0px]',
        )}
      />
      <div
        ref={boxRef}
        onMouseDown={handleBoxMouseDown}
        // Two parts that read as one panel: the bar (HeaderSearchField) and the dropdown
        // under it. Closing, the bar shrinks back into the field and the dropdown fades
        // where it stands, so nothing travels up the page.
        className={cn(
          'absolute top-0 right-0 w-full transition-[width,top]',
          open ? ['-top-1.5 w-[400px]', PANEL_EASE_CLASS] : PANEL_CLOSE_EASE_CLASS,
        )}
      >
        <HeaderSearchField
          open={open}
          value={value}
          placeholder={t('placeholder')}
          placeholders={placeholders}
          shortcutHit={hitKey === 'shortcut'}
          inputRef={inputRef}
          listId={listId}
          activeId={navigation.activeId}
          onChange={handleValueChange}
          onFocus={openPanel}
          onKeyDown={handleInputKeyDown}
          onClear={handleClear}
        />
        {/* The dropdown grows out of the bar; closing, it fades in place and folds unseen. */}
        <div
          inert={!open}
          className={cn('grid', open ? DROPDOWN_OPEN_CLASS : DROPDOWN_CLOSED_CLASS)}
        >
          <div
            className={cn(
              'min-h-0 overflow-hidden rounded-b-[14px] border-x border-b border-solid border-v2-border bg-v2-surface-secondary dark:border-v2-surface-tertiary',
              'shadow-[0px_16px_40px_-12px_rgba(0,0,0,0.3),0px_4px_4px_0px_rgba(0,0,0,0.1)]',
              'dark:shadow-[0px_24px_60px_-18px_rgba(0,0,0,0.75),0px_4px_4px_0px_rgba(0,0,0,0.25)]',
            )}
          >
            {/* Nothing loads until the first open; after it the content stays for the fold-back. */}
            {entranceKey > 0 ? (
              <SearchBody
                query={value}
                state={search}
                onQueryChange={setValue}
                onClose={handleResultOpened}
                entranceKey={entranceKey}
                renderList={renderList}
              />
            ) : null}
            <SearchHints hitKey={hitKey} canClear={Boolean(value) || tab !== 'all'} />
          </div>
        </div>
      </div>
    </div>
  );
}
